import type { Capabilities, NavCounts } from "./wire.js";
import type { UserRole } from "./vocabulary.js";

export type NavigationDevice = "phone" | "tablet" | "desktop";

/**
 * The five menu groups, in the order the menu draws them (TRBUD-439; design v3
 * "The menu, final"). One word each, so a group head reads at 13px uppercase and
 * the rail's divider can carry it as a tooltip.
 */
export const NAVIGATION_GROUPS = [
  { id: "work", label: "Work" },
  { id: "fleet", label: "Fleet" },
  { id: "supplies", label: "Supplies" },
  { id: "reports", label: "Reports" },
  { id: "setup", label: "Setup" },
] as const;
export type NavigationGroup = (typeof NAVIGATION_GROUPS)[number]["id"];

export interface NavigationItem {
  path: string;
  label: string;
  shortLabel: string;
  capability?: keyof Capabilities;
  heldRoles?: readonly UserRole[];
  devices: readonly NavigationDevice[];
  group?: NavigationGroup;
  hint?: string;
  /**
   * Other routes this destination owns, so the menu marks it active there too.
   *
   * A pattern is a path whose segments must match the start of the pathname;
   * a `:name` segment matches any one segment. The most specific match wins
   * across the whole table (`navigationMatchScore`), so Saved reports'
   * `/reports/:id` never takes `/reports/spend` from Spend & cash.
   */
  activeFor?: readonly string[];
}

const ALL_DEVICES = ["phone", "tablet", "desktop"] as const;
const WIDE_ONLY = ["tablet", "desktop"] as const;
const MANAGEMENT = ["admin", "manager", "processor"] as const;

/**
 * The single source for application navigation and device policy.
 *
 * Ordered by group and, inside a group, by how often a page is opened (design
 * v3). The order is load-bearing twice: `navigationGroups` keeps it inside each
 * group, and the phone's More sheet lists what the bottom bar leaves in this
 * order.
 *
 * `heldRoles` is deliberately evaluated against the complete grant set. The
 * primary role is presentation only and must never hide a route granted by a
 * second role.
 */
export const NAVIGATION_ITEMS: readonly NavigationItem[] = [
  // Work
  { path: "/work-orders", label: "Work orders", shortLabel: "Queue", group: "work", hint: "Repairs jobs queue", capability: "searchWorkOrders", devices: ALL_DEVICES },
  { path: "/requests", label: "Requests", shortLabel: "Requests", group: "work", hint: "Maintenance request inbox", heldRoles: [...MANAGEMENT, "requester"], devices: ALL_DEVICES },
  { path: "/overview", label: "Repair overview", shortLabel: "Overview", group: "work", hint: "Repairs mechanics timeline Gantt", heldRoles: MANAGEMENT, devices: ALL_DEVICES },
  { path: "/attendance", label: "Attendance review", shortLabel: "Attendance", group: "work", hint: "Team shifts clock in approval exceptions", heldRoles: ["admin", "manager"], capability: "setLabourRate", devices: ALL_DEVICES },
  { path: "/time-clock", label: "Time clock", shortLabel: "Clock", group: "work", hint: "Attendance punch in out", heldRoles: ["mechanic"], devices: ALL_DEVICES },
  // Fleet
  { path: "/assets", label: "Assets", shortLabel: "Assets", group: "fleet", hint: "Trucks trailers fleet map location", capability: "viewAssets", devices: ALL_DEVICES },
  { path: "/board", label: "Status board", shortLabel: "Board", group: "fleet", hint: "Availability fleet status", capability: "viewStatusBoard", devices: ALL_DEVICES },
  // Supplies
  { path: "/parts", label: "Parts", shortLabel: "Parts", group: "supplies", hint: "Inventory stock catalog shelves", capability: "viewParts", devices: ALL_DEVICES },
  { path: "/purchase-orders", label: "Purchase orders", shortLabel: "Orders", group: "supplies", hint: "Orders receipts suppliers variance", capability: "viewParts", devices: ALL_DEVICES },
  /*
   * Vendor directory access belongs to management. Mechanics use the limited
   * supplier picker when raising a job-linked purchase order.
   */
  { path: "/vendors", label: "Vendors", shortLabel: "Vendors", group: "supplies", hint: "Shops suppliers accounts credits", capability: "viewVendors", heldRoles: MANAGEMENT, devices: WIDE_ONLY },
  /*
   * TRBUD-467: the AI receipt accuracy page lives under `/reports/` with no menu
   * item of its own and belongs to the Review queue it is opened from, so the
   * Saved reports `/reports/:id` pattern does not claim it. Every role that may
   * open it holds `confirmInvoice`, so this entry is visible to all of them.
   */
  { path: "/review", label: "Review queue", shortLabel: "Review", group: "supplies", hint: "Invoices receipts documents", capability: "confirmInvoice", devices: ALL_DEVICES, activeFor: ["/reports/receipt-accuracy"] },
  // Reports: three pages, no in-page tab strip (design v4 #1).
  { path: "/reports/spend", label: "Spend & cash", shortLabel: "Spend", group: "reports", hint: "Spend costs cash payments charts", capability: "viewReports", devices: ALL_DEVICES, activeFor: ["/reports", "/reports/mainx-review"] },
  { path: "/reports/work", label: "Work & downtime", shortLabel: "Downtime", group: "reports", hint: "Downtime labor work orders charts", capability: "viewReports", devices: ALL_DEVICES },
  { path: "/reports/saved", label: "Saved reports", shortLabel: "Saved", group: "reports", hint: "Saved reports builder dashboards", capability: "viewReports", devices: ALL_DEVICES, activeFor: ["/reports/new", "/reports/:id"] },
  // Setup
  { path: "/settings/connections", label: "Connections & bots", shortLabel: "Connections", group: "setup", hint: "API integrations automation bot activity monitoring health", capability: "manageCollectors", devices: ALL_DEVICES },
  { path: "/config", label: "Configuration", shortLabel: "Config", group: "setup", hint: "Worksites locations workflows setup", capability: "configuration", devices: WIDE_ONLY },
  { path: "/settings", label: "Settings", shortLabel: "Settings", group: "setup", hint: "Account appearance users preferences", devices: ALL_DEVICES },
] as const;

/**
 * A person who holds the requester role and nothing else: a driver.
 *
 * One definition for the three places that ask it: the menu (which shows such a
 * person only Requests, Settings and the time clock), the landing redirect, and
 * the work-orders page, which sends them back to Requests (Eden, 22 October
 * 2026). Anyone holding another role as well is staff and keeps the staff
 * screens. No roles at all is not a requester.
 */
export function isRequesterOnly(roles: readonly UserRole[]): boolean {
  return roles.length > 0 && roles.every((role) => role === "requester");
}

export function isNavigationItemVisible(
  item: NavigationItem,
  roles: readonly UserRole[],
  can: Capabilities,
): boolean {
  if (item.capability && !can[item.capability]) return false;
  if (item.path === "/time-clock" && roles.some((role) => role === "admin" || role === "manager")) return false;
  if (item.heldRoles && !item.heldRoles.some((role) => roles.includes(role))) return false;
  return true;
}

export function visibleNavigationItems(
  roles: readonly UserRole[],
  can: Capabilities,
  device?: NavigationDevice,
): NavigationItem[] {
  const requesterOnly = isRequesterOnly(roles);
  return NAVIGATION_ITEMS.filter(
    (item) =>
      (!requesterOnly || item.path === "/requests" || item.path === "/settings" || item.path === "/time-clock") &&
      isNavigationItemVisible(item, roles, can) &&
      (!device || item.devices.includes(device)),
  );
}

function segmentsOf(path: string): string[] {
  return path.split("/").filter((segment) => segment !== "");
}

/**
 * How specifically one path pattern covers a pathname: 0 when it does not, and
 * otherwise the matched segment count first and the literal (non-`:param`)
 * segment count second, so `/reports/spend` (two literal) outranks
 * `/reports/:id` (one), which outranks `/reports` (one segment). A pathname may
 * run on past the pattern (a detail route keeps its parent active), but a
 * segment must match whole: `/work-orders-elsewhere` is not under `/work-orders`.
 */
function patternScore(pattern: string, pathname: string): number {
  const want = segmentsOf(pattern);
  const have = segmentsOf(pathname);
  if (want.length === 0 || have.length < want.length) return 0;
  let literal = 0;
  for (let index = 0; index < want.length; index += 1) {
    const segment = want[index]!;
    if (segment.startsWith(":")) continue;
    if (segment !== have[index]) return 0;
    literal += 1;
  }
  return want.length * 100 + literal;
}

/** How specifically this destination owns a pathname; 0 when it does not. */
export function navigationMatchScore(item: NavigationItem, pathname: string): number {
  return Math.max(
    patternScore(item.path, pathname),
    ...(item.activeFor ?? []).map((pattern) => patternScore(pattern, pathname)),
  );
}

/**
 * Is this destination the active one?
 *
 * Given a PATH, the plain rule detail routes have always used: the path itself
 * or anything under it. Given an ITEM, its `activeFor` routes count too, and it
 * is active only where no other destination owns the pathname more
 * specifically, so two menu entries are never marked current at once.
 */
export function isNavigationPathActive(target: string | NavigationItem, pathname: string): boolean {
  if (typeof target === "string") {
    return pathname === target || pathname.startsWith(`${target}/`);
  }
  const score = navigationMatchScore(target, pathname);
  if (score === 0) return false;
  return !NAVIGATION_ITEMS.some(
    (other) => other.path !== target.path && navigationMatchScore(other, pathname) > score,
  );
}

/** Filter before grouping/search: hidden destinations must never become search hits. */
export function navigationGroups(roles: readonly UserRole[], can: Capabilities, query = "") {
  const search = query.trim().toLocaleLowerCase();
  const items = visibleNavigationItems(roles, can).filter((item) =>
    !search || `${item.label} ${item.hint ?? ""}`.toLocaleLowerCase().includes(search));
  return NAVIGATION_GROUPS.map((group) => ({ ...group,
    items: items.filter((item) => (item.group ?? "work") === group.id),
  })).filter((group) => group.items.length > 0);
}

/** The destination that owns a pathname: the most specific match, `activeFor` included. */
export function activeNavigationPath(items: readonly NavigationItem[], pathname: string): string | undefined {
  return items
    .map((item) => ({ item, score: navigationMatchScore(item, pathname) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.path.length - a.item.path.length)[0]?.item.path;
}

/* ── Menu counts — TRBUD-443 ───────────────────────────────────────── */

/**
 * Which figure of `NavCounts` a destination carries, or null for a destination
 * that carries none. Three pages only: Requests, Review queue and Attendance
 * review, each counting what waits on it by its own rule (see `NavCounts`).
 */
export function navCountKey(path: string): keyof NavCounts | null {
  switch (path) {
    case "/requests": return "requests";
    case "/review": return "review";
    case "/attendance": return "attendance";
    default: return null;
  }
}

/**
 * The count a destination should DRAW: a positive number, or null.
 *
 * Null covers four states that all draw nothing, on purpose: a destination with
 * no count, counts not loaded yet (or failed), a page the server says is not
 * yours (`null` in `NavCounts`), and zero. A failed read must not draw a
 * stale or invented number, and zero has nothing to say.
 */
export function navCountFor(path: string, counts: NavCounts | null | undefined): number | null {
  const key = navCountKey(path);
  if (key === null || !counts) return null;
  const value = counts[key];
  return typeof value === "number" && value > 0 ? value : null;
}

/** "99+" above 99, as the notification bell writes it. */
export function navCountText(count: number): string {
  return count > 99 ? "99+" : String(count);
}

/**
 * A counted entry's accessible name: "Requests, 3 waiting". The plain label
 * when nothing is drawn, so the name a person hears always matches what is on
 * screen. Uses the same capped text as the badge, so a screen reader and the
 * eye never disagree about "99+".
 */
export function navCountName(label: string, count: number | null): string {
  return count === null ? label : `${label}, ${navCountText(count)} waiting`;
}

/**
 * Does this person's menu hold any counted destination? The client asks the
 * server for counts only then, so a mechanic's or a viewer's browser never polls
 * for figures it would never draw. Read from the same table the menu draws.
 */
export function hasCountedNavigation(roles: readonly UserRole[], can: Capabilities): boolean {
  return visibleNavigationItems(roles, can).some((item) => navCountKey(item.path) !== null);
}

/** The menu group a pathname belongs to, for the page head's eyebrow. */
export function navigationGroupFor(pathname: string): (typeof NAVIGATION_GROUPS)[number] | undefined {
  const path = activeNavigationPath(NAVIGATION_ITEMS, pathname);
  const item = NAVIGATION_ITEMS.find((candidate) => candidate.path === path);
  if (!item) return undefined;
  return NAVIGATION_GROUPS.find((group) => group.id === (item.group ?? "work"));
}

const COMPACT_PRIORITY: Readonly<Record<UserRole, readonly string[]>> = {
  admin: ["/work-orders", "/board", "/assets", "/review"],
  manager: ["/work-orders", "/board", "/assets", "/review"],
  processor: ["/work-orders", "/requests", "/review", "/board"],
  mechanic: ["/work-orders", "/assets", "/parts", "/time-clock"],
  viewer: ["/board", "/assets", "/work-orders"],
  requester: ["/requests", "/time-clock"],
};
const ROLE_NAVIGATION_PRECEDENCE: readonly UserRole[] = [
  "admin",
  "manager",
  "processor",
  "mechanic",
  "viewer",
  "requester",
];

/** At most four task destinations; the shell reserves the fifth slot for More. */
export function compactPrimaryItems(
  roles: readonly UserRole[],
  can: Capabilities,
): NavigationItem[] {
  const visible = visibleNavigationItems(roles, can, "phone");
  const orderedPaths = ROLE_NAVIGATION_PRECEDENCE
    .filter((role) => roles.includes(role))
    .flatMap((role) => COMPACT_PRIORITY[role]);
  const uniquePaths = [...new Set(orderedPaths)];
  return uniquePaths
    .map((path) => visible.find((item) => item.path === path))
    .filter((item): item is NavigationItem => item !== undefined)
    .slice(0, 4);
}
